import React from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  ChevronRight,
  Loader2,
  Send,
  FileText,
  User,
  Phone,
  IndianRupee,
} from "lucide-react";
import WhatsAppIconPNG from "../../../assets/Icons/Whatsapp.svg";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
} from "../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const WhatsAppHeroIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  box-shadow: 0 8px 20px rgba(37, 211, 102, 0.35);
  margin-bottom: 12px;
`;

const HeroCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const OptionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionBtn = styled.button`
  width: 100%;
  padding: 16px 18px;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fafafa;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: rgba(37, 211, 102, 0.35);
    background: rgba(37, 211, 102, 0.06);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(37, 211, 102, 0.12);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OptionInner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const OptionIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #128c7e;
  background: rgba(37, 211, 102, 0.12);
`;

const OptionText = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionTitle = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
`;

const OptionDesc = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.52);
  line-height: 1.45;
`;

export interface WhatsAppShareOption {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void | Promise<void>;
  showSpinner?: boolean;
}

export interface WhatsAppShareOptionsModalProps {
  badge: string;
  subtitle?: string;
  onClose: () => void;
  loading?: boolean;
  options: WhatsAppShareOption[];
  maxWidth?: number;
  zIndex?: number;
}

export const WhatsAppShareOptionsModal: React.FC<WhatsAppShareOptionsModalProps> = ({
  badge,
  subtitle = "Choose how you want to share",
  onClose,
  loading = false,
  options,
  maxWidth = 480,
  zIndex,
}) => {
  return createPortal(
    <ModalOverlay onClick={onClose} style={zIndex ? { zIndex } : undefined}>
      <ModalShell
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroCenter>
              <WhatsAppHeroIcon>
                <img src={WhatsAppIconPNG} width={30} height={30} alt="WhatsApp" />
              </WhatsAppHeroIcon>
              <HeroText>
                <HeroBadge>{badge}</HeroBadge>
                <ModalTitle>Share via WhatsApp</ModalTitle>
                <ModalSubtitle>{subtitle}</ModalSubtitle>
              </HeroText>
            </HeroCenter>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody>
          <OptionsStack>
            {options.map((option, index) => (
              <OptionBtn
                key={index}
                type="button"
                onClick={option.onClick}
                disabled={loading}
              >
                <OptionInner>
                  <OptionIcon>
                    {loading && option.showSpinner ? <Spinner size={22} /> : option.icon}
                  </OptionIcon>
                  <OptionText>
                    <OptionTitle>{option.title}</OptionTitle>
                    <OptionDesc>{option.description}</OptionDesc>
                  </OptionText>
                  {!loading || !option.showSpinner ? (
                    <ChevronRight size={18} color="rgba(23,23,23,0.35)" />
                  ) : null}
                </OptionInner>
              </OptionBtn>
            ))}
          </OptionsStack>
        </ModalBody>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};

// ── Confirmation Modal (Direct Send to Number flow with PDF) ─────────────────

const ConfirmCard = styled.div`
  background: #fbfbfb;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ConfirmRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
`;

const ConfirmLabel = styled.span`
  color: rgba(23, 23, 23, 0.55);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ConfirmValue = styled.span`
  color: #171717;
  font-weight: 700;
  text-align: right;
  word-break: break-word;
`;

const AttachmentPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.2);
  border-radius: 12px;
  color: #c94daa;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
`;

const ConfirmHeroIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  box-shadow: 0 8px 20px rgba(37, 211, 102, 0.35);
  margin-bottom: 12px;
`;

const ModalActionButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  padding: 0 20px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.14);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #111827;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfirmSendButton = styled.button`
  padding: 0 24px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.95) 0%, rgba(206, 86, 176, 0.95) 100%);
  box-shadow: 0 4px 16px rgba(201, 77, 170, 0.3);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(201, 77, 170, 0.45);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

export interface WhatsAppShareConfirmModalProps {
  badge?: string;
  title?: string;
  subtitle?: string;
  customerName?: string;
  customerPhone?: string;
  documentNumber?: string;
  grandTotal?: string | number;
  pdfFileName?: string;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
  maxWidth?: number;
  zIndex?: number;
}

export const WhatsAppShareConfirmModal: React.FC<WhatsAppShareConfirmModalProps> = ({
  badge,
  title = "Share via WhatsApp",
  subtitle,
  customerName,
  customerPhone,
  documentNumber,
  grandTotal,
  pdfFileName,
  loading = false,
  onConfirm,
  onClose,
  maxWidth = 460,
  zIndex,
}) => {
  return createPortal(
    <ModalOverlay onClick={loading ? undefined : onClose} style={zIndex ? { zIndex } : undefined}>
      <ModalShell
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroCenter>
              <ConfirmHeroIcon>
                <img src={WhatsAppIconPNG} width={28} height={28} alt="WhatsApp" />
              </ConfirmHeroIcon>
              <HeroText>
                {badge && <HeroBadge>{badge}</HeroBadge>}
                <ModalTitle>{title}</ModalTitle>
                {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
              </HeroText>
            </HeroCenter>
            <CloseBtn type="button" onClick={onClose} disabled={loading} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody>
          <ConfirmCard>
            {customerName && (
              <ConfirmRow>
                <ConfirmLabel>
                  <User size={14} /> Customer
                </ConfirmLabel>
                <ConfirmValue>{customerName}</ConfirmValue>
              </ConfirmRow>
            )}

            {customerPhone && (
              <ConfirmRow>
                <ConfirmLabel>
                  <Phone size={14} /> WhatsApp Number
                </ConfirmLabel>
                <ConfirmValue>{customerPhone}</ConfirmValue>
              </ConfirmRow>
            )}

            {documentNumber && (
              <ConfirmRow>
                <ConfirmLabel>
                  <FileText size={14} /> Document No.
                </ConfirmLabel>
                <ConfirmValue>{documentNumber}</ConfirmValue>
              </ConfirmRow>
            )}

            {grandTotal !== undefined && grandTotal !== null && Number(grandTotal) > 0 && (
              <ConfirmRow>
                <ConfirmLabel>
                  <IndianRupee size={14} /> Grand Total
                </ConfirmLabel>
                <ConfirmValue>
                  ₹{Number(grandTotal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </ConfirmValue>
              </ConfirmRow>
            )}

            {pdfFileName && (
              <AttachmentPill>
                <FileText size={16} />
                <span>Attachment: {pdfFileName}</span>
              </AttachmentPill>
            )}
          </ConfirmCard>

          <ModalActionButtons>
            <CancelButton type="button" onClick={onClose} disabled={loading}>
              Cancel
            </CancelButton>
            <ConfirmSendButton
              type="button"
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size={16} />
                  <span>Sending PDF...</span>
                </>
              ) : (
                <>
                  <Send size={15} />
                  <span>Share on WhatsApp</span>
                </>
              )}
            </ConfirmSendButton>
          </ModalActionButtons>
        </ModalBody>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};

